import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Calendar, MapPin, Users, QrCode, Settings, LogOut, Globe, Shield, Crown, Clock, Sparkles, Trash2, Sun, Moon, Wand2 } from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: '', date: '', location: '', language: 'en' });
  const [creating, setCreating] = useState(false);
  const [deletingEventId, setDeletingEventId] = useState(null);
  const { user, logout, clearFirstLogin } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const { adminDarkMode, toggleAdminDarkMode, adminTheme: th } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for isFirstLogin - only true if explicitly set to 'true'
  // After first login, it's cleared (removed) or set to 'false'
  const [showWelcome] = useState(() => {
    const fromState = location.state?.isFirstLogin;
    const fromStorage = localStorage.getItem('isFirstLogin');
    // Only show welcome if explicitly true
    return fromState === true || fromStorage === 'true';
  });

  useEffect(() => {
    fetchEvents();
    // Clear first login flag after showing welcome message
    if (showWelcome) {
      // Clear immediately to prevent showing on refresh
      localStorage.setItem('isFirstLogin', 'false');
      const timer = setTimeout(() => {
        clearFirstLogin();
        window.history.replaceState({}, document.title);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  // Update language preference on backend when changed
  const handleLanguageChange = async (newLanguage) => {
    setLanguage(newLanguage);
    try {
      await axios.put(`${API}/auth/language`, { language: newLanguage });
    } catch (error) {
      console.error('Failed to update language preference:', error);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/events`, newEvent);
      toast.success('Event created!');
      setShowCreateDialog(false);
      setNewEvent({ name: '', date: '', location: '', language: 'en' });
      fetchEvents();
    } catch (error) {
      const errorCode = error.response?.data?.detail;
      if (errorCode === 'PLAN_LIMIT_EVENTS_FREE') {
        toast.error(t('planLimitEventsFree'));
      } else if (errorCode === 'PLAN_LIMIT_EVENTS_PRO') {
        toast.error(t('planLimitEventsPro'));
      } else {
        toast.error(t('planLimitReached'));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEvent = async (eventId, eventName) => {
    if (!window.confirm(`${t('confirmDelete')} "${eventName}"?`)) {
      return;
    }
    
    setDeletingEventId(eventId);
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success(t('eventDeleted'));
      fetchEvents();
    } catch (error) {
      toast.error(t('deleteError'));
    } finally {
      setDeletingEventId(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen admin-bg p-4 md:p-8 transition-colors duration-300">
      {/* Header */}
      <header className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold admin-accent">
            {t('myEvents')}
          </h1>
          <p className="admin-text-secondary mt-1">
            {showWelcome ? t('welcome') : t('welcomeBack')} {user?.name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Theme Toggle */}
          <button
            onClick={toggleAdminDarkMode}
            className="p-2 rounded-lg transition-all duration-200 admin-card admin-card-hover admin-icon"
            title={adminDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            data-testid="theme-toggle-btn"
          >
            {adminDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          {user?.is_admin && (
            <Button
              variant="outline"
              onClick={() => navigate('/admin')}
              className="admin-card admin-accent"
              data-testid="admin-panel-btn"
            >
              <Shield className="h-4 w-4 mr-2" />
              Admin
            </Button>
          )}
          <Select value={language} onValueChange={handleLanguageChange}>
            <SelectTrigger 
              className="w-[140px] admin-card admin-text"
              data-testid="language-select"
            >
              <Globe className="h-4 w-4 mr-2 admin-icon" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">{t('english')}</SelectItem>
              <SelectItem value="pt">{t('portuguese')}</SelectItem>
              <SelectItem value="fr">{t('french')}</SelectItem>
              <SelectItem value="es">{t('spanish')}</SelectItem>
              <SelectItem value="el">{t('greek')}</SelectItem>
              <SelectItem value="ru">{t('russian')}</SelectItem>
              <SelectItem value="it">{t('italian')}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="admin-card admin-text"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            {t('logout')}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto">
        {/* Create Event Button */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button
              className="mb-8 admin-accent-bg font-semibold rounded-full px-6 py-5 transition-all duration-300"
              data-testid="create-event-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              {t('createEvent')}
            </Button>
          </DialogTrigger>
          <DialogContent className="admin-card admin-text">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl admin-accent">
                {t('createEvent')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="admin-text-secondary">{t('eventName')}</Label>
                <Input
                  value={newEvent.name}
                  onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                  className="admin-input"
                  placeholder="Ana & Miguel Wedding"
                  required
                  data-testid="event-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="admin-text-secondary">{t('eventDate')}</Label>
                <Input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  className="admin-input"
                  required
                  data-testid="event-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="admin-text-secondary">{t('eventLocation')}</Label>
                <Input
                  value={newEvent.location}
                  onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                  className="admin-input"
                  placeholder="Grand Ballroom, City Hotel"
                  required
                  data-testid="event-location-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="admin-text-secondary">{t('language')}</Label>
                <Select value={newEvent.language} onValueChange={(val) => setNewEvent({ ...newEvent, language: val })}>
                  <SelectTrigger className="admin-input" data-testid="event-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">{t('english')}</SelectItem>
                    <SelectItem value="pt">{t('portuguese')}</SelectItem>
                    <SelectItem value="fr">{t('french')}</SelectItem>
                    <SelectItem value="es">{t('spanish')}</SelectItem>
                    <SelectItem value="el">{t('greek')}</SelectItem>
                    <SelectItem value="ru">{t('russian')}</SelectItem>
                    <SelectItem value="it">{t('italian')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                disabled={creating}
                className="w-full admin-accent-bg font-semibold rounded-full py-5"
                data-testid="create-event-submit-btn"
              >
                {creating ? 'Creating...' : t('createEvent')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Events Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="admin-card rounded-xl h-64 animate-pulse" />
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full admin-card flex items-center justify-center">
              <Calendar className="h-10 w-10 admin-text-secondary" />
            </div>
            <p className="admin-text-secondary text-lg">{t('noEvents')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
            {events.map((event) => {
              const tierColors = {
                free: 'bg-slate-200 text-slate-600',
                premium: 'bg-blue-100 text-blue-600',
                pro: 'bg-amber-100 text-amber-600'
              };
              const getTierName = (tierId) => {
                switch (tierId) {
                  case 'free': return t('free');
                  case 'premium': return t('premium');
                  case 'pro': return t('pro');
                  default: return t('free');
                }
              };
              const effectiveTier = event.tier;
              
              return (
              <div
                key={event.id}
                className="group relative overflow-hidden rounded-2xl admin-card transition-all duration-500 hover:shadow-lg"
                data-testid={`event-card-${event.id}`}
              >
                <div className="relative p-6">
                  {/* Tier Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${tierColors[effectiveTier] || tierColors.free}`}>
                      {effectiveTier !== 'free' && <Crown className="h-3 w-3" />}
                      {getTierName(effectiveTier)}
                      {event.is_trial && (
                        <span className="flex items-center gap-0.5 ml-1 text-amber-600">
                          <Clock className="h-2.5 w-2.5" />
                          {t('trial')}
                        </span>
                      )}
                    </span>
                    {effectiveTier === 'free' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/subscription?event_id=${event.id}`);
                        }}
                        className="admin-accent text-xs px-2 py-1 h-auto"
                      >
                        <Sparkles className="h-3 w-3 mr-1" />
                        {t('upgrade')}
                      </Button>
                    )}
                  </div>
                  
                  <h3 className="font-heading text-xl font-semibold admin-text mb-2 group-hover:opacity-80 transition-colors">
                    {event.name}
                  </h3>
                  <div className="space-y-2 admin-text-secondary text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{event.date ? format(new Date(event.date), 'PPP') : 'No date'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span>{event.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>{event.guest_count} {t('guests')}</span>
                    </div>
                  </div>

                  {/* Continue editing — shown when an invitation draft exists and is not published */}
                  {event.invitation_status?.exists && !event.invitation_status?.is_published && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/events/${event.id}/invitation-editor`);
                      }}
                      className="mt-4 w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-dashed border-emerald-300 dark:border-emerald-700/50 bg-emerald-50/50 dark:bg-emerald-900/10 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-400 dark:hover:border-emerald-600 transition-all group/cont"
                      data-testid={`event-continue-editing-${event.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="relative flex h-2 w-2 shrink-0">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-50"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                        </span>
                        <div className="text-left min-w-0">
                          <div className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 truncate">
                            Continue editing
                          </div>
                          <div className="text-[10px] text-emerald-600/70 dark:text-emerald-400/60 truncate">
                            Draft · {(event.invitation_status?.template_id || '').replace(/-/g, ' ').replace(/\bv\d+$/i, '').trim() || 'invitation'}
                          </div>
                        </div>
                      </div>
                      <Wand2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 transition-transform group-hover/cont:scale-110 group-hover/cont:rotate-12" />
                    </button>
                  )}

                  <div className="flex gap-2 mt-6">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/events/${event.id}/qr`)}
                      className="flex-1 admin-card admin-text-secondary"
                      data-testid={`event-qr-btn-${event.id}`}
                    >
                      <QrCode className="h-4 w-4 mr-1" />
                      {t('viewQR')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/events/${event.id}`)}
                      className="flex-1 admin-accent-bg"
                      data-testid={`event-manage-btn-${event.id}`}
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      {t('manage')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEvent(event.id, event.name);
                      }}
                      disabled={deletingEventId === event.id}
                      className="border-red-300 text-red-500 hover:bg-red-50"
                      data-testid={`event-delete-btn-${event.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
